import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { CompanyNameCombineInputSelector, resetCompanyNameCombineInput, setCompanyNameCombineInput, setCompanyNameCombineInputExpand, setCompanyNames, setDistinceCompanyNames, setName, setSelectedCompanyNameCombineId, setTriggerRefreshCompanyNames } from './reducers/CompanyNameCombineInputReducer';
import CompanyNameCombineApi from './api/CompanyNameCombineApi';
import CompanyNameCombineModel from './models/CompanyNameCombineModel';
import { setTriggerRefreshData } from './reducers/CompanyNameCombineSearchReducer';
import { setTriggerSearchClassification } from '../Classification/reducers/ClassificationReducer';

const CompanyNameCombineInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const CompanyNameCombineInputViewData = useSelector(CompanyNameCombineInputSelector);

    useEffect(() => {
        if (CompanyNameCombineInputViewData.triggerSave > 0) {
            save();
        }
    }, [CompanyNameCombineInputViewData.triggerSave]);

    useEffect(() => {
        CompanyNameCombineApi.GetAllCompanyNames()
            .then((response: BaseResponse<string[]>) => {
                if (response.success) {
                    dispatch(setDistinceCompanyNames(response.data))
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
    }, [CompanyNameCombineInputViewData.triggerRefreshCompanyNames])

    const refreshInput = () => {
        dispatch(resetCompanyNameCombineInput());
        dispatch(setSelectedCompanyNameCombineId(""));
        dispatch(setTriggerRefreshData((new Date()).getTime()));
        dispatch(setTriggerRefreshCompanyNames((new Date()).getTime()));
    }

    useEffect(() => {
        if (CompanyNameCombineInputViewData.selectedCompanyNameCombineId.length > 0) {
            selectCompanyNameCombineById(CompanyNameCombineInputViewData.selectedCompanyNameCombineId);
        }
    }, [CompanyNameCombineInputViewData.selectedCompanyNameCombineId])

    const selectCompanyNameCombineById = (id: string) => {
        dispatch(showLoading(true));
        CompanyNameCombineApi.GetById({
            id: id
        }).then((response: BaseResponse<CompanyNameCombineModel>) => {
            if (response.success) {
                dispatch(setCompanyNameCombineInput(response.data));
                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const save = () => {
        try {
            validate();

            CompanyNameCombineApi.Save(CompanyNameCombineInputViewData.companyNameCombineInput)
                .then((response: BaseResponse<CompanyNameCombineModel>) => {
                    if (response.success) {
                        if (CompanyNameCombineInputViewData.companyNameCombineInput.id.length === 0) {
                            Utility.Alert.Success("Company Name Combine added successful");
                        } else {
                            Utility.Alert.Success("Company Name Combine updated successful");
                        }

                        dispatch(setTriggerSearchClassification((new Date()).getTime()));

                        refreshInput();
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (CompanyNameCombineInputViewData.companyNameCombineInput.name.length === 0) {
            throw "Name is required";
        }
    }

    const getCompanyNamesValue = () => {
        if (CompanyNameCombineInputViewData.companyNameCombineInput.companyNames.length === 0) {
            return [];
        }
        return CompanyNameCombineInputViewData.companyNameCombineInput.companyNames.split(",");
    }

    return (
        <>
            {
                <CustomAccordion
                    title="Input"
                    defaultOpen={true}
                    className="top-quick-input"
                    open={CompanyNameCombineInputViewData.companyNameCombineInputExpand}
                    onOpenChange={(isOpen: boolean) => {
                        dispatch(setCompanyNameCombineInputExpand(isOpen));
                    }}
                >
                    <Grid container>
                        <Grid item lg={10} xs={12}>
                            <Grid container className='cargo-input-component'>
                                <Grid xs={10} lg={9}>
                                    <TextField
                                        label="Name"
                                        fullWidth
                                        value={CompanyNameCombineInputViewData.companyNameCombineInput.name ? CompanyNameCombineInputViewData.companyNameCombineInput.name : ""}
                                        onChange={(event) => {
                                            dispatch(setName(event.target.value));
                                        }}
                                        className='cargo-input-component tonnage-input-full-tc'
                                    />
                                </Grid>
                                <Grid item xs={2} lg={3}>
                                    <Tooltip title="Reset (Alt + R)">
                                        <Button
                                            className="clear-input-button"
                                            tabIndex={-1}
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                refreshInput();
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid>
                                <Autocomplete
                                    className='cargo-input-component'
                                    options={CompanyNameCombineInputViewData.companyNames}
                                    getOptionLabel={(option: string) => {
                                        return option
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Company Names" />}
                                    value={getCompanyNamesValue()}
                                    multiple={true}
                                    onChange={(event: React.SyntheticEvent, value: string[], reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                        dispatch(setCompanyNames(value.join(",")));
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <Tooltip title="Alt + S">
                                <Button
                                    onClick={save}
                                    variant="outlined"
                                >
                                    Save (s)
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </CustomAccordion>
            }
        </>
    )
};

export default CompanyNameCombineInput;